import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  PRODUCT_SEARCH_SUGGESTION_LIMIT,
  buildProductSearchSuggestions,
  normalizeProductSearchText,
} from '../utils/productSearch';
import useProductSearchSettings from '../hooks/useProductSearchSettings';

const PRODUCT_SEARCH_HISTORY_KEY = 'productSearchHistory:v1';
const PRODUCT_SEARCH_HISTORY_LIMIT = 20;
const PRODUCT_SEARCH_MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const DEFAULT_PRODUCT_SEARCH_SUGGESTION_VISIBILITY = {
  desktop: true,
  mobile: false,
};

const normalizeSuggestionVisibilityFlag = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  return fallback;
};

const resolveSuggestionVisibility = (value, isMobile) => {
  if (typeof value === 'boolean') return value;
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : DEFAULT_PRODUCT_SEARCH_SUGGESTION_VISIBILITY;

  return isMobile
    ? normalizeSuggestionVisibilityFlag(
      source.mobile,
      DEFAULT_PRODUCT_SEARCH_SUGGESTION_VISIBILITY.mobile
    )
    : normalizeSuggestionVisibilityFlag(
      source.desktop,
      DEFAULT_PRODUCT_SEARCH_SUGGESTION_VISIBILITY.desktop
    );
};

const getIsMobileSearchViewport = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(PRODUCT_SEARCH_MOBILE_MEDIA_QUERY).matches;
  }
  return Number(window.innerWidth || 1024) < 768;
};

const useIsMobileSearchViewport = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileSearchViewport);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewport = () => setIsMobileViewport(getIsMobileSearchViewport());
    const mediaQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia(PRODUCT_SEARCH_MOBILE_MEDIA_QUERY)
      : null;

    updateViewport();
    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }
    if (mediaQuery?.addListener) {
      mediaQuery.addListener(updateViewport);
      return () => mediaQuery.removeListener(updateViewport);
    }
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  return isMobileViewport;
};

const readProductSearchHistory = (historyKey = PRODUCT_SEARCH_HISTORY_KEY) => {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(historyKey) || '[]');
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
};

const shouldRememberProductSearch = (value) => {
  const normalized = normalizeProductSearchText(value);
  return normalized.length >= 2 && !/^\d+$/.test(normalized);
};

const writeProductSearchHistory = (value, historyKey = PRODUCT_SEARCH_HISTORY_KEY) => {
  if (!shouldRememberProductSearch(value)) return readProductSearchHistory(historyKey);
  const normalizedValue = normalizeProductSearchText(value);
  const current = readProductSearchHistory(historyKey);
  const next = [
    String(value || '').trim(),
    ...current.filter((entry) => normalizeProductSearchText(entry) !== normalizedValue),
  ].slice(0, PRODUCT_SEARCH_HISTORY_LIMIT);

  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(historyKey, JSON.stringify(next));
    } catch (error) {
      return next;
    }
  }
  return next;
};

const assignInputValue = (input, value) => {
  if (!input) return;
  const valueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;

  if (valueSetter) {
    valueSetter.call(input, value);
  } else {
    input.value = value;
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
};

const ProductSearchSuggestionsPopup = ({
  activeIndex,
  anchorRef,
  id,
  isOpen,
  onActiveIndexChange,
  onSelect,
  suggestions = [],
}) => {
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition({
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [isOpen, suggestions.length, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  if (
    !isOpen ||
    !suggestions.length ||
    !position ||
    typeof document === 'undefined' ||
    !document.body
  ) {
    return null;
  }

  return createPortal(
    <div
      id={id}
      role="listbox"
      className="fixed z-[25] max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-xl"
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
      }}
    >
      {suggestions.map((suggestion, index) => {
        const isActive = index === activeIndex;
        return (
          <button
            key={suggestion}
            id={`${id}-option-${index}`}
            type="button"
            role="option"
            aria-selected={isActive}
            className={`block w-full px-3 py-2 text-left ${
              isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-800 hover:bg-gray-50'
            }`}
            onMouseEnter={() => onActiveIndexChange(index)}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              onSelect(suggestion);
            }}
          >
            {suggestion}
          </button>
        );
      })}
    </div>,
    document.body
  );
};

export const useProductSearchSuggestions = ({
  products = [],
  suggestions,
  suggestionLimit = PRODUCT_SEARCH_SUGGESTION_LIMIT,
  value = '',
  historyEnabled = true,
  historyKey = PRODUCT_SEARCH_HISTORY_KEY,
  searchMode,
}) => {
  const [history, setHistory] = useState(() =>
    historyEnabled ? readProductSearchHistory(historyKey) : []
  );

  useEffect(() => {
    if (!historyEnabled) {
      setHistory([]);
      return;
    }
    setHistory(readProductSearchHistory(historyKey));
  }, [historyEnabled, historyKey]);

  const resolvedSuggestions = useMemo(
    () => buildProductSearchSuggestions(products, {
      limit: suggestionLimit,
      query: value,
      history: historyEnabled ? history : [],
      searchMode,
      suggestionValues: Array.isArray(suggestions) ? suggestions : [],
    }),
    [products, suggestions, suggestionLimit, value, historyEnabled, history, searchMode]
  );

  const rememberSearch = useCallback((nextValue = value) => {
    if (!historyEnabled) return;
    setHistory(writeProductSearchHistory(nextValue, historyKey));
  }, [historyEnabled, historyKey, value]);

  return { resolvedSuggestions, rememberSearch };
};

const ProductSearchInput = React.forwardRef(({
  products = [],
  suggestions,
  suggestionLimit = PRODUCT_SEARCH_SUGGESTION_LIMIT,
  historyEnabled = true,
  historyKey = PRODUCT_SEARCH_HISTORY_KEY,
  searchMode,
  suggestionVisibility = DEFAULT_PRODUCT_SEARCH_SUGGESTION_VISIBILITY,
  containerClassName,
  rightAdornment,
  listId,
  type = 'text',
  ...inputProps
}, ref) => {
  const generatedId = useId().replace(/:/g, '-');
  const resolvedListId = listId || `product-search-${generatedId}`;
  const inputRef = useRef(null);
  const [draftValue, setDraftValue] = useState(() => inputProps.defaultValue || '');
  const [isFocused, setIsFocused] = useState(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const currentValue = inputProps.value ?? draftValue;
  const productSearchSettings = useProductSearchSettings();
  const resolvedSearchMode = searchMode || productSearchSettings.searchMode;
  const isMobileViewport = useIsMobileSearchViewport();
  const suggestionsEnabled = resolveSuggestionVisibility(suggestionVisibility, isMobileViewport);
  const { resolvedSuggestions, rememberSearch } = useProductSearchSuggestions({
    products,
    suggestions,
    suggestionLimit,
    value: currentValue,
    historyEnabled,
    historyKey,
    searchMode: resolvedSearchMode,
  });
  const showSuggestions = suggestionsEnabled && isFocused && isSuggestionsOpen && resolvedSuggestions.length > 0;

  const setInputRef = useCallback((node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  useEffect(() => {
    if (!showSuggestions) {
      setActiveSuggestionIndex(-1);
      return;
    }
    setActiveSuggestionIndex((index) => (
      index >= resolvedSuggestions.length ? -1 : index
    ));
  }, [resolvedSuggestions.length, showSuggestions]);

  const handleBlur = (event) => {
    setIsFocused(false);
    setIsSuggestionsOpen(false);
    rememberSearch(event.target.value);
    inputProps.onBlur?.(event);
  };

  const handleChange = (event) => {
    setDraftValue(event.target.value);
    setIsSuggestionsOpen(suggestionsEnabled);
    setActiveSuggestionIndex(-1);
    inputProps.onChange?.(event);
  };

  const selectSuggestion = (suggestion) => {
    setDraftValue(suggestion);
    assignInputValue(inputRef.current, suggestion);
    inputRef.current?.focus();
    setIsSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
    rememberSearch(suggestion);
  };

  const handleFocus = (event) => {
    setIsFocused(true);
    setIsSuggestionsOpen(suggestionsEnabled);
    inputProps.onFocus?.(event);
  };

  const handleKeyDown = (event) => {
    if (suggestionsEnabled && resolvedSuggestions.length && event.key === 'ArrowDown') {
      event.preventDefault();
      setIsSuggestionsOpen(true);
      setActiveSuggestionIndex((index) => (
        index < resolvedSuggestions.length - 1 ? index + 1 : 0
      ));
      return;
    }

    if (suggestionsEnabled && resolvedSuggestions.length && event.key === 'ArrowUp') {
      event.preventDefault();
      setIsSuggestionsOpen(true);
      setActiveSuggestionIndex((index) => (
        index > 0 ? index - 1 : resolvedSuggestions.length - 1
      ));
      return;
    }

    if (event.key === 'Escape' && showSuggestions) {
      event.preventDefault();
      setIsSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (event.key === 'Enter' && showSuggestions && activeSuggestionIndex >= 0) {
      event.preventDefault();
      selectSuggestion(resolvedSuggestions[activeSuggestionIndex]);
      return;
    }

    if (event.key === 'Enter') {
      rememberSearch(event.currentTarget.value);
    }
    inputProps.onKeyDown?.(event);
  };

  const input = (
    <input
      ref={setInputRef}
      type={type}
      autoComplete={inputProps.autoComplete ?? 'off'}
      {...inputProps}
      aria-activedescendant={
        showSuggestions && activeSuggestionIndex >= 0
          ? `${resolvedListId}-option-${activeSuggestionIndex}`
          : undefined
      }
      aria-autocomplete={suggestionsEnabled ? 'list' : 'none'}
      aria-controls={showSuggestions ? resolvedListId : undefined}
      aria-expanded={showSuggestions}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      role={inputProps.role || 'combobox'}
    />
  );

  return (
    <>
      {containerClassName || rightAdornment ? (
        <div className={containerClassName || 'relative'}>
          {input}
          {rightAdornment}
        </div>
      ) : input}
      <ProductSearchSuggestionsPopup
        activeIndex={activeSuggestionIndex}
        anchorRef={inputRef}
        id={resolvedListId}
        isOpen={showSuggestions}
        onActiveIndexChange={setActiveSuggestionIndex}
        onSelect={selectSuggestion}
        suggestions={resolvedSuggestions}
      />
    </>
  );
});

ProductSearchInput.displayName = 'ProductSearchInput';

export default ProductSearchInput;
